/* eslint-disable @reatom/unit-naming-rule */
import { action } from '@reatom/framework'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { delugeIdAtom } from '@root/entities/auth'
import { selectedNetworkAtom } from '@root/entities/network'
import { profileModelAtom } from '@root/entities/profile'
import { referralCodeAtom, type ReferralCode } from '@root/entities/referral'
import { toSharedErrorCode } from '@root/features/secure-storage/secure-storage-error'
import { ApiError } from '@root/shared/api'
import { UnknownError } from '@root/shared/core/Error/Error'
import { reatomRequest } from '@root/shared/lib'
import {
  showErrorCodeSnackbarAction,
  showErrorSnackbarAction,
} from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { processChangeReferralCodeTransactionAction } from './referral-code-requests'

import type { WalletId } from '@root/entities/wallet'

export const reatomChangeReferralCodeForm = () => {
  const executeAction = action(
    async (
      ctx,
      {
        referralCode,
        walletId,
      }: { referralCode: ReferralCode; walletId: WalletId },
    ) => {
      const currentReferralCode = ctx.get(referralCodeAtom)
      const network = ctx.get(selectedNetworkAtom)
      const delugeId = ctx.get(delugeIdAtom)

      if (!currentReferralCode || !delugeId) {
        return Result.fail(UnknownError.fromUnknown('Invalid input'))
      }

      const result = Result.unify(
        await ctx.schedule(() =>
          processChangeReferralCodeTransactionAction(ctx, {
            currentReferralCode,
            referralCode,
            walletId,
            network,
            delugeId,
          }),
        ),
      )

      return result.pipe(
        Result.map(() => ({
          referralCode,
        })),
      )
    },
  )

  const requestStateAtom = reatomRequest({
    execAction: executeAction,
    onResult: (ctx, result) => {
      Result.match(result, {
        onFailure: (error) =>
          error instanceof UnknownError || ApiError.is(error)
            ? showErrorSnackbarAction(ctx, error)
            : showErrorCodeSnackbarAction(ctx, toSharedErrorCode(error)),
        onSuccess: ({ referralCode }) => {
          const profileModel = ctx.get(profileModelAtom)
          profileModel.setReferralCode(ctx, referralCode)
          showSnackbarAction(ctx, {
            intent: 'success',
            text: i18next.t(
              'referral.referral_code_has_been_successfully_changed',
            ),
          })
        },
      })
    },
  })

  return {
    requestState: requestStateAtom,
    change: requestStateAtom.execute,
  }
}
